import {
  Button,
  Flex,
  Grid,
  Heading,
  Item,
  Picker,
  Text,
  TextArea,
  TextField,
  View,
  Well,
  CheckboxGroup,
  Checkbox,
  InlineAlert,
  FileTrigger,
  ProgressCircle,
  LabeledValue,
} from "@adobe/react-spectrum";
import React, { useEffect, useState } from "react";
import { FormData, withKey } from "../data";
import { getValidationMessages, eventSeriesThumbnail, isValid, submit, removeProfilePicture, validateHeadshotImage } from "../formHelper";
import { DateTimeZoneFormFields } from "./DateTimeZoneFormFields";
import actionWebInvoke from "../utils";
import actions from '../config.json';
import './Form.css';

/**
 * Component to render the ExL Event submission form.
 */
export const Form = ({ ims, oktaAuth }) => {
  // State to hold form submission status
  const [data, setData] = useState({
    loading: false,
    success: null,
    error: null,
  });

  const [formData, setFormData] = useState({
    events: []
  });

  // State to hold form data
  const [form, setForm] = useState({
    eventStartTime: null,
    eventTimezone: null,
    promoStartTime: null,
    promoStartTimezone: null,
    promoEndTime: null,
    promoEndTimezone: null,
    eventTitle: "",
    eventDescription: "",
    eventSeries: "",
    roles: [],
    skillLevels: [],
    productFocus: [],
    ctaLabel: "",
    ctaLink: "",
    email: "",
    eventImage: "",
    eventLocation: {
      type: "",
      city: ""
    },
    speakers: []
  });

  // State for managing profile picture uploads
  const [profilePictures, setProfilePictures] = useState({});
  const [products, setProducts] = useState([]);

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const user = await oktaAuth.getUser();
        setForm({ ...form, email: user.email });
      } catch (error) {
        // Silent error handling for production
      }
    };
    fetchUserInfo();
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const products = await FormData.products();
        setProducts(products);
      } catch (error) {
        // Silent error handling for production
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    const fetchFormData = async () => {      
      setFormData(await actionWebInvoke(actions["form-data"]));
    };
    fetchFormData();
  }, []);

  const [selectedTimezone, setSelectedTimezone] = useState("");

  return (
    <View
      alignSelf="start"
      maxWidth="800px"
      marginStart="auto"
      marginEnd="auto"
    >
      <Grid
        areas={[
          "event-details-header",
          "event-date",
          "adv-start-date",
          "adv-end-date",
          "title",
          "description",
          "roles",
          "skills",
          "event-type",
          "event-location",
          "series",
          "speakers",
          "product",
          "CTA",
          "link",
          "email",
          "submit",
          "result",
        ]}
        columns={["1fr"]}
        gap="size-100"
      >
        <View gridArea="event-details-header">
          <Heading level={2}>Event details</Heading>
        </View>

        {/* Event Date, Time, and Timezone Fields */}
        <View gridArea="event-date">
          <DateTimeZoneFormFields
            label="What time does the event start?"
            disableTimezone={false}
            onChange={(value, timezone) => {
              setForm((prevForm) => ({
                ...prevForm, 
                eventStartTime: value, 
                eventTimezone: timezone,
                promoStartTimezone: timezone,
                promoEndTimezone: timezone
              }));
            }}           
          />
        </View>

        <View gridArea="adv-start-date" marginTop="size-100">
          <DateTimeZoneFormFields
            label="When would you like this event to be promoted on Experience League?"
            disableTimezone={true}
            selectedTimeZone={form.eventTimezone}
            onChange={(value, timezone) => {
              setForm((prevForm) => ({
                ...prevForm,
                promoStartTime: value,
                promoStartTimezone: timezone,
              }));
            }}
          />
        </View>

        <View gridArea="adv-end-date" marginTop="size-100">
          <DateTimeZoneFormFields
            label="When would you like the promotion for this event to end?"
            disableTimezone={true}
            selectedTimeZone={form.eventTimezone}
            onChange={(value, timezone) => {
              setForm((prevForm) => ({
                ...prevForm,
                promoEndTime: value,
                promoEndTimezone: timezone,
              }));
            }}
          />
        </View>

        <InlineAlert variant="notice" isHidden={!(form.promoEndTime > form.eventStartTime)} marginTop="size-300">
          Continue promoting the event after the event ends? Stop promotion date is AFTER the event date.
        </InlineAlert>

        <View gridArea="title">
          <TextField
            label="Event title"
            value={form.eventTitle}
            isRequired
            necessityIndicator="icon"
            inputMode="text"
            width="100%"
            onChange={(value) => {
              setForm({ ...form, eventTitle: value });
            }}
          />
        </View>

        <View gridArea="description">
          <TextArea
            label="Event description (copy/paste is disabled)"
            value={form.eventDescription}
            isRequired
            necessityIndicator="icon"
            inputMode="text"
            width="100%"
            onPaste={(e) => { 
              if (window.location.search.includes("paste")) {
                // allow paste
              } else {
                e.preventDefault();
              }
            }}
            onChange={(value) => {
              setForm({ ...form, eventDescription: value });
            }}
          />
          <div style={{ paddingLeft: "1.5rem" }}>
            <p className="description-limit-note">
              Limit your description to 219 characters and no line breaks. This
              field does not allow copying and pasting of text.
            </p>
          </div>
        </View>

        <View gridArea="roles" marginTop="size-100">
          <CheckboxGroup
            label="What role(s) does this event apply to?"
            value={form.roles}
            onChange={(value) => {
              setForm({ ...form, roles: value });
            }}
          >
            <Checkbox value="Business user">
              <Flex direction="column" gap="size-50">
                <Text fontWeight="semibold">Business user</Text>
                <LabeledValue label="Responsible for utilizing Adobe products to achieve daily job functions, complete tasks, and achieve business objectives"></LabeledValue>
              </Flex>
            </Checkbox>
            <Checkbox value="Developer">
              <Flex direction="column" gap="size-50">
                <Text fontWeight="semibold">Developer</Text>
                <LabeledValue label="Responsible for engineering Adobe products' implementation, integration, data-modeling, data engineering, and other technical skills"></LabeledValue>
              </Flex>
            </Checkbox>
            <Checkbox value="Administrator">
              <Flex direction="column" gap="size-50">
                <Text fontWeight="semibold">Administrator</Text>
                <LabeledValue label="Responsible for the technical operations, configuration, permissions, management, and support needs of Adobe products"></LabeledValue>
              </Flex>
            </Checkbox>
            <Checkbox value="Business leader">
              <Flex direction="column" gap="size-50">
                <Text fontWeight="semibold">Business leader</Text>
                <LabeledValue label="Responsible for owning the digital strategy and accelerating value through Adobe products"></LabeledValue>
              </Flex>
            </Checkbox>
          </CheckboxGroup>
        </View>

        <View gridArea="skills" marginBottom="size-100">
          <CheckboxGroup
            label="What skill level(s) does this event most apply to?"
            value={form.skillLevels}
            onChange={(value) => {
              setForm({ ...form, skillLevels: value });
            }}
          >
            <Checkbox value="Beginner">
              <Flex direction="column" gap="size-50">
                <Text fontWeight="semibold">Beginner</Text>
                <LabeledValue label="You're just starting out with the selected product and are looking to grow your skills"></LabeledValue>
              </Flex>
            </Checkbox>
            <Checkbox value="Intermediate">
              <Flex direction="column" gap="size-50">
                <Text fontWeight="semibold">Intermediate</Text>
                <LabeledValue label="You have a moderate understanding of the selected product, but have room to level up your skills"></LabeledValue>
              </Flex>
            </Checkbox>
            <Checkbox value="Experienced">
              <Flex direction="column" gap="size-50">
                <Text fontWeight="semibold">Experienced</Text>
                <LabeledValue label="You know the selected product inside and out and use your advanced skills to achieve complex objectives"></LabeledValue>
              </Flex>
            </Checkbox>
          </CheckboxGroup>
        </View>

        <View gridArea="event-type">
          <Picker
            label="Event type"
            placeholder="Select event type..."
            selectedKey={form.eventLocation.type}
            items={withKey(["virtual", "in-person"])}
            isRequired
            necessityIndicator="icon"
            width="100%"
            onSelectionChange={(value) => {
              setForm({ 
                ...form, 
                eventLocation: {
                  ...form.eventLocation,
                  type: value,
                  city: value === "virtual" ? "" : form.eventLocation.city
                }
              });
            }}
          >
            {(item) => <Item key={item.key}>{item.key === "virtual" ? "Virtual" : "In Person"}</Item>}
          </Picker>
        </View>

        <View gridArea="event-location">
          {form.eventLocation.type === "in-person" && (
            <TextField
              label="Event location (city)"
              value={form.eventLocation.city}
              isRequired
              necessityIndicator="icon"
              inputMode="text"
              width="100%"
              onChange={(value) => {
                setForm({ 
                  ...form, 
                  eventLocation: {
                    ...form.eventLocation,
                    city: value
                  }
                });
              }}
            />
          )}
        </View>

        <View gridArea="series">
          <Picker
            label="If this event is part of a recurring event series, select from the below options"
            placeholder="Select an event series or leave blank..."
            selectedKey={form.eventSeries || ""}
            items={withKey(["", ...formData.events])}
            width="100%"
            onSelectionChange={(value) => {
              setForm({ ...form, 
                eventSeries: value?.toLowerCase() === "no series" ? "" : (value || ""),
                eventImage: value ? eventSeriesThumbnail(value) : ""
               });
            }}
          >
            {(item) => <Item key={item.key}>{item.key === "" ? "No series" : item.key}</Item>}
          </Picker>
        </View>        

        <View gridArea="speakers" marginTop="size-100">
          <LabeledValue label="Add speakers for your event"></LabeledValue>
          
          {/* Future functionality note 

            <Text marginBottom="size-50" color="neutral-700">
            
            </Text>


          <div className="speaker-note">
            <Text fontSize="size-100" color="neutral-600">
              📝 <strong>Note:</strong> Displaying speaker information on{" "}
              <a 
                href="https://experienceleague.adobe.com/en/events" 
                target="_blank" 
                rel="noopener noreferrer"
                className="speaker-note-link"
              >
                Experience League Events page
              </a>{" "}
              is currently under development, they might not be displayed as of now, thanks for your patience.
            </Text>
          </div>
          */}
          
          {form.speakers.map((speaker, index) => (
            <Well key={index} marginTop="size-300" padding="size-300">
              <Flex direction="column" gap="size-300">
                {/* Speaker Header with Remove Button */}
                <Flex direction="row" justifyContent="space-between" alignItems="center">
                  <Heading level={4} margin="0">
                    Speaker {index + 1}
                  </Heading>
                  <Button
                    variant="negative"
                    size="S"
                    onPress={() => {
                      const updatedSpeakers = form.speakers.filter((_, i) => i !== index);
                      setForm({ ...form, speakers: updatedSpeakers });
                    }}
                  >
                    Remove Speaker
                  </Button>
                </Flex>
                
                {/* Speaker Name, Title, and Company - Single Row */}
                <Flex direction="row" gap="size-200" alignItems="start">
                  <TextField
                    label="Name"
                    value={speaker.name}
                    isRequired
                    necessityIndicator="icon"
                    width="100%"
                    onChange={(value) => {
                      const updatedSpeakers = [...form.speakers];
                      updatedSpeakers[index] = { ...speaker, name: value };
                      setForm({ ...form, speakers: updatedSpeakers });
                    }}
                  />
                  <TextField
                    label="Title"
                    value={speaker.title || ""}
                    width="100%"
                    onChange={(value) => {
                      const updatedSpeakers = [...form.speakers];
                      updatedSpeakers[index] = { ...speaker, title: value };
                      setForm({ ...form, speakers: updatedSpeakers });
                    }}
                  />
                  <TextField
                    label="Company"
                    value={speaker.company || ""}
                    width="100%"
                    onChange={(value) => {
                      const updatedSpeakers = [...form.speakers];
                      updatedSpeakers[index] = { ...speaker, company: value };
                      setForm({ ...form, speakers: updatedSpeakers });
                    }}
                  />
                </Flex>

                {/* Profile Picture Upload - 3 Column Grid Layout */}
                <Grid columns={["1fr", "1fr", "1fr"]} gap="size-200" alignItems="start">
                  {/* Column 1: Upload Button */}
                  <Flex direction="column" gap="size-200" alignItems="start">
                    <Text fontSize="size-100" color="neutral-600">
                      Profile Picture
                    </Text>
                    <FileTrigger
                      key={`file-trigger-${index}`}
                      acceptedFileTypes={['image/jpeg', 'image/jpg', 'image/png']}
                      onSelect={async (files) => {
                        if (files && files.length > 0) {
                          const file = files[0];
                          
                          // Industry-standard headshot validations
                          try {
                            const validationResult = await validateHeadshotImage(file);
                            
                            if (!validationResult.isValid) {
                              alert(validationResult.errorMessage);
                              return;
                            }
                          } catch (error) {
                            alert('Error validating image: ' + error.message);
                            return;
                          }
                          
                          // Use functional updates to avoid stale state issues
                          setProfilePictures(prev => ({
                            ...prev,
                            [index]: file
                          }));
                        }
                      }}
                      allowsMultiple={false}
                    >
                      <Button variant="secondary" size="S">
                        Upload Photo
                      </Button>
                    </FileTrigger>
                  </Flex>
                  
                  {/* Column 2: Photo Preview */}
                  <Flex direction="column" gap="size-200" alignItems="start">
                    <Text fontSize="size-100" color="neutral-600">
                      Preview
                    </Text>
                    {profilePictures[index] ? (
                      <div className="speaker-profile-container">
                        <img
                          src={URL.createObjectURL(profilePictures[index])}
                          alt="Profile preview"
                          className="speaker-profile-image"
                        />
                      </div>
                    ) : (
                      <div className="speaker-profile-placeholder">
                        <Text fontSize="size-100" color="neutral-500">No image</Text>
                      </div>
                    )}
                  </Flex>
                  
                  {/* Column 3: Image Details & Actions */}
                  <Flex direction="column" gap="size-200" alignItems="start">
                    <Text fontSize="size-100" color="neutral-600">
                      Details
                    </Text>
                    {profilePictures[index] ? (
                      <Flex direction="column" gap="size-100">
                        {/* Image Specifications */}
                        <Flex direction="column" gap="size-50">
                          <Text fontSize="size-75" color="neutral-600">
                            Size: {(profilePictures[index].size / 1024 / 1024).toFixed(1)} MB
                          </Text>
                          <Text fontSize="size-75" color="neutral-600">
                            Type: {profilePictures[index].type.split('/')[1].toUpperCase()}
                          </Text>
                        </Flex>
                        
                        {/* Clear Button */}
                        <Button
                          variant="secondary"
                          size="S"
                          onPress={() => {
                            setProfilePictures(prev => ({ ...prev, [index]: undefined }));
                          }}
                        >
                          Clear
                        </Button>
                        
                        {/* File Name */}
                        <Text fontSize="size-75" color="positive-600" maxWidth="200px">
                          ✓ {profilePictures[index].name}
                        </Text>
                      </Flex>
                    ) : (
                      <Flex direction="column" gap="size-100">
                        <Text fontSize="size-100" color="neutral-600" fontWeight="semibold">
                          Requirements:
                        </Text>
                        <Flex direction="column" gap="size-50">
                          <Text fontSize="size-50" color="neutral-600">
                            • JPG or PNG format
                          </Text>
                          <Text fontSize="size-50" color="neutral-600">
                            • Up to 5 MB
                          </Text>
                          <Text fontSize="size-50" color="neutral-600">
                            • At least 200×200 px
                          </Text>
                          <Text fontSize="size-50" color="neutral-600">
                            • Portrait or landscape
                          </Text>
                          <Text fontSize="size-50" color="neutral-600">
                            • Face should be centered
                          </Text>
                        </Flex>
                      </Flex>
                    )}
                  </Flex>
                </Grid>
              </Flex>
            </Well>
          ))}
          
          {/* Add Speaker Button */}
          <Flex direction="row" alignItems="center" gap="size-200" marginTop="size-100">
            <Button
              variant="secondary"
              onPress={() => {
                setForm({
                  ...form,
                  speakers: [...form.speakers, { name: "", title: "", company: "" }]
                });
              }}
            >
              <Flex direction="row" alignItems="center" gap="size-100">
                <span>+</span>
                <span>Add Speaker</span>
              </Flex>
            </Button>
            
            {form.speakers.length > 0 && (
              <Text color="neutral-600" fontSize="size-100">
                {form.speakers.length} speaker{form.speakers.length !== 1 ? 's' : ''} added
              </Text>
            )}
          </Flex>
        </View>

        <View gridArea="product">
          <CheckboxGroup
            label="Primary product focus"
            value={form.productFocus}
            isRequired
            necessityIndicator="icon"
            onChange={(value) => {
              setForm({ ...form, productFocus: value });
            }}
          >
            <Grid columns={["1fr", "1fr"]} columnGap="100px">
              {products.map((product) => {
                return (
                  <Checkbox key={product} value={product}>
                    {product}
                  </Checkbox>
                );
              })}
            </Grid>
          </CheckboxGroup>
        </View>

        <View gridArea="CTA">
          <Picker
            label="Event button label"
            selectedKey={form.ctaLabel}
            items={withKey(FormData.ctaLabels)}
            isRequired
            necessityIndicator="icon"
            width="100%"
            onSelectionChange={(value) => {
              setForm({ ...form, ctaLabel: value });
            }}
          >
            {(item) => <Item key={item.key}>{item.key}</Item>}
          </Picker>
        </View>

        <View gridArea="link">
          <TextField
            label="Event button URL"
            value={form.ctaLink}
            inputMode="url"
            isRequired
            necessityIndicator="icon"
            width="100%"
            onChange={(value) => {
              setForm({ ...form, ctaLink: value });
            }}
          />
        </View>

        <View gridArea="email">
          <TextField
            label="Your Adobe e-mail address"
            value={form.email}
            inputMode="email"
            isRequired
            necessityIndicator="icon"
            width="100%"
            onChange={(value) => {
              setForm({ ...form, email: value });
            }}
          />
        </View>

        {/* Submit Button */}
        <View gridArea="submit">
          <Flex direction="row" gap="size-200">
            <Button
              variant="cta"
              isDisabled={!isValid(form) || data.loading || data.success}
              onPress={() => {
                submit(form, ims, oktaAuth, setData, profilePictures);
              }}
            >
              {data.loading ? 'Processing...' : 'Submit Event'}
            </Button>

            {data.loading && (
              <Flex direction="row" alignItems="center" gap="size-200">
                <ProgressCircle aria-label="Processing…" isIndeterminate />
                <Text fontSize="size-100">Processing event submission...</Text>
              </Flex>
            )}

            <Text
              alignSelf="center"
              isHidden={isValid(form)}
              marginTop="size-100"
            >
              You are missing some required fields! Please correct the
              information in the form before submitting.
              <ul>
                {getValidationMessages(form).map((message, i) => {
                  return <li key={i}>{message}</li>;
                })}
              </ul>
            </Text>
          </Flex>
        </View>

        {/* Form Submission Result */}
        <View gridArea="result">
          {/* ERROR */}
          {data.error && (
            <>
              <div className="error-container">
                <Heading level={2} marginBottom="size-200" className="error-heading">
                  Error while submitting event details!
                </Heading>
                
                <p className="error-text">
                  We could not process your submission, please look at the error message(s) below.
                </p>
                
                <div className="error-divider">
                  <strong className="error-label">
                    Error Details:
                  </strong>
                  
                  <div className="error-box">
                    {data.error
                      ?.filter((error) => error && error.trim())
                      .map((error, i) => (
                        <div key={i} className={`error-item ${i < data.error.length - 1 ? 'error-item-with-margin' : 'error-item-no-margin'}`}>
                          {error}
                        </div>
                      ))}
                  </div>
                  
                  <p className="common-text error-text-override">
                    If the problem persists, please contact the{" "}
                    <a 
                      href="mailto:Grp-ExLevents@adobe.com"
                      className="error-link"
                    >
                      Experience League team
                    </a>{" "}
                    and provide the above error message.
                  </p>
                </div>
              </div>
            </>
          )}

          {/* SUCCESS */}
          {data.success && (
            <>
              <div className="success-container">
                <Heading level={2} marginBottom="size-200">
                  Event submitted successfully!
                </Heading>
                <p className="success-text">
                  You will receive an email confirmation shortly.
                </p>
                
                <div className="success-divider">
                  <strong className="success-label">
                    After Your Event:
                  </strong>
                  <p className="common-text success-text-override">
                    Submit your event recording using the{" "}
                    <a
                      href="https://adobe.ly/ExLvideoupload"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="success-link"
                    >
                      Experience League video submission form
                    </a>.
                  </p>
                </div>
              </div>
            </>
          )}
        </View>
      </Grid>
    </View>
  );
};
